import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/customer/lib/utils'

/**
 * Snoots-flavoured pill badge. Flat brand-colour fill, borderless.
 *
 * `variant` picks the fill; foreground colour is set to satisfy WCAG
 * AA per DESIGN.md (yellow/pink/orange → black text, green/blue →
 * white text).
 *
 * `size` controls typography + padding. `default` is the standalone
 * chip (register-flow, page headers); `sm` and `xs` step down for
 * inline use inside body-text rows or dense tables where the default
 * pill dominates the row.
 */
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 rounded-full whitespace-nowrap [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        neutral: 'bg-neutral-200 text-foreground',
        success: 'bg-green text-background',
        warning: 'bg-orange text-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        info: 'bg-baby-blue text-foreground',
      },
      size: {
        default: 'text-button-sm px-3 py-1',
        sm: 'text-[10px] font-bold uppercase tracking-[0.02em] px-2 py-0.5',
        xs: 'text-[9px] font-bold uppercase tracking-[0.02em] px-1.5 py-px',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'default',
    },
  }
)

function Badge({
  className,
  variant,
  size,
  render,
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        className: cn(badgeVariants({ variant, size }), className),
      },
      props
    ),
    render,
    state: {
      slot: 'badge',
      variant,
    },
  })
}

export { Badge, badgeVariants }
